// ─────────────────────────────────────────────────────────────────────
// THE ACTIVE CONTEXT, AS THE TRANSPORT NEEDS IT — a leaf, on purpose (RBAC-3).
//
// `http.ts` must attach the context to every request and `impersonation.ts`
// owns the richer state, so the two fields the transport reads live here where
// both can reach them without importing each other.
//
// TWO FIELDS, NOT ONE. The token says «this request is made inside a context»;
// the organization says WHICH club's data the request is about, and the API
// resolves the tenant from the ordinary header. Keeping them together is what
// stops a request going out with a context and no tenant — authenticated,
// authorized for an organization, and addressed to nothing.
//
// IN MEMORY ONLY, like every other bearer token in this app
// (`lib/auth/clubSession.ts` states the rule). A context is short, explicit
// and meant to end with the page; a copy in storage would outlive the intent
// behind it and turn «I operated that club for ten minutes» back into a
// standing power. Moving between /sa and the portal is a client-side
// transition precisely so this can stay in memory — see `app/surface.ts`.
// ─────────────────────────────────────────────────────────────────────

import { beginScopeTransition } from './scopeTransition';

interface Context {
  token: string;
  organizationId: string;
}

let active: Context | null = null;

/**
 * How the transport resolves the organization a request is addressed to —
 * `customerContext().tenantId`, registered by that module because it imports
 * this one and the dependency cannot point the other way. Until it has
 * registered, the context's own organization stands in.
 */
let resolvedTenant: (() => string | null) | null = null;
export function registerResolvedTenant(fn: () => string | null): void {
  resolvedTenant = fn;
}
const addressNow = () => (resolvedTenant ? resolvedTenant() : (active?.organizationId ?? null));

export function setImpersonationContext(next: Context | null): void {
  // STARTING, SWITCHING OR ENDING A CONTEXT MOVES THE ADDRESS every request
  // carries — from the operator's own club to the customer's, or back — and a
  // write still in flight from before must not follow it
  // (lib/api/scopeTransition.ts). Measured on the resolved address, so a
  // context started on the staff surface, where it is not what the transport
  // addresses, opens nothing.
  const before = addressNow();
  active = next;
  beginScopeTransition(before, addressNow());
}

export function getImpersonationToken(): string | null {
  return active?.token ?? null;
}

/**
 * The tenant every request should be addressed to while a context is active.
 *
 * Null when there is none, so the transport falls back to the club session's
 * scope and an ordinary sign-in is completely unaffected.
 */
export function getImpersonationTenantId(): string | null {
  return active?.organizationId ?? null;
}

// ─────────────────────────────────────────────────────────────────────
// THE SERVER REFUSED THE CONTEXT — SO IT IS OVER HERE TOO.
//
// `resolveImpersonationContext` answers 401 with `impersonation_expired`,
// `_revoked`, `_wrong_actor` or `_invalid`, and says why in its own comment: a
// presented-but-bad context is a refusal rather than a shrug, because «the
// caller believes they are operating a customer's organization, and silently
// dropping that belief is how somebody acts on the wrong data».
//
// THE BROWSER WAS DOING EXACTLY THAT. `apiFetch` routes a 401 by the token
// FAMILY that authenticated it — `sps_`, `spm_` — and an Entra token matches
// neither, so nothing happened at all: the banner kept naming the
// organization, the portal stayed mounted, and every request kept failing
// while the operator was told they were still inside a context. The 45-minute
// expiry had no local effect whatsoever.
//
// This is the seam that fixes it, and it lives in the leaf for the same reason
// the token does: `http.ts` must be able to report a refusal, and
// `impersonation.ts` owns the state and imports `apiFetch`. A direct call
// either way is a cycle.
// ─────────────────────────────────────────────────────────────────────

let onRefused: (() => void) | null = null;

/** Registered once by `lib/api/impersonation.ts`, which owns the state. */
export function setImpersonationRefusedHandler(fn: (() => void) | null): void {
  onRefused = fn;
}

/**
 * Called by the transport when the API refuses the context it presented —
 * a 401 `impersonation_*`, or a 403 `entra_required` (#1030): a context on a
 * session Microsoft did not prove is refused just as surely.
 *
 * A no-op when nothing is active, so a late answer to a request made just
 * before «Avslutt» cannot resurrect anything or fire the portal's return
 * twice.
 */
export function reportImpersonationRefused(): void {
  if (!active) return;
  onRefused?.();
}
